import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ChatParticipant, ChatMessage } from "../../types";
import {
  fetchAdminChats, fetchAdminChatThread, adminSendChat, adminGradeWork, chatPhotoUrl,
} from "../../utils/api";
import { AppHeader, Icon } from "../common/ui";
import ChatThread from "../common/ChatThread";
import AccessPanel from "./AccessPanel";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

/** The identifiers that tell two students apart and let the admin grant access.
 *
 * A first name alone ("Lin") is not enough: it is not unique, and it is not
 * something `/grant` accepts. Order mirrors services/student_ref.py — @username,
 * then e-mail, then the Telegram id — so the web list and the bot's review-chat
 * notifications name a person the same way.
 */
function IdentityLine({ p, style }: { p: ChatParticipant; style?: React.CSSProperties }) {
  const { t } = useTranslation();
  const parts: string[] = [];
  if (p.username) parts.push(`@${p.username}`);
  else if (p.email) parts.push(p.email);
  if (p.telegram_id) parts.push(`${t("chat.userId")} ${p.telegram_id}`);
  // Guest accounts carry none of the three — fall back to the internal id so
  // the row still points at exactly one account.
  if (parts.length === 0) parts.push(`${t("chat.noIdentifiers")} · ${p.user_id.slice(0, 8)}`);

  return (
    <div
      className="t-xs muted-2"
      title={parts.join(" · ")}
      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...style }}
    >
      {parts.join(" · ")}
    </div>
  );
}

function Conversation({ token, participant, isRu, onBack, theme, onThemeChange }: {
  token: string; participant: ChatParticipant; isRu: boolean; onBack: () => void;
  theme: "dark" | "light"; onThemeChange: (t: "dark" | "light") => void;
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [grading, setGrading] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setMessages(await fetchAdminChatThread(participant.user_id, token)); } catch { /* silent */ }
  }, [participant.user_id, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 20_000); return () => clearInterval(id); }, [load]);

  const onSend = async (text: string, photo?: string) => {
    await adminSendChat(participant.user_id, token, { body: text, photo_base64: photo });
    await load();
  };

  const grade = async (messageId: string, status: "passed" | "failed") => {
    setGrading(messageId);
    try { await adminGradeWork(messageId, status, undefined, token); await load(); }
    finally { setGrading(null); }
  };

  const renderUnder = (m: ChatMessage) => {
    if (m.kind !== "work" || m.status !== "submitted") return null;
    return (
      <div className="row" style={{ gap: 8, marginTop: 6 }}>
        <button className="btn btn-glass" onClick={() => grade(m.id, "failed")} disabled={grading === m.id}
          style={{ flex: 1, color: "var(--error)", paddingBlock: 8 }}>
          <Icon name="x" size={15} /> {t("inbox.needsWork")}
        </button>
        <button className="btn btn-primary" onClick={() => grade(m.id, "passed")} disabled={grading === m.id}
          style={{ flex: 1, paddingBlock: 8 }}>
          <Icon name="check" size={15} /> {t("inbox.passed")}
        </button>
      </div>
    );
  };

  return (
    <div className="beeapp__scroll" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onBack} title={participant.name} showLang={false} />
      <div className="stack" style={{ "--gap": "8px", width: "100%", maxWidth: 640, margin: "0 auto", padding: "0 16px 6px", boxSizing: "border-box" } as React.CSSProperties}>
        <IdentityLine p={participant} />
        {/* Granting access is done here, next to the conversation where the
            student is actually asking for it. */}
        <AccessPanel userId={participant.user_id} token={token} />
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", width: "100%", maxWidth: 640, margin: "0 auto", padding: "6px 16px 12px", boxSizing: "border-box" }}>
        <ChatThread
          messages={messages}
          isRu={isRu}
          senderIsAdmin
          onSend={onSend}
          photoUrl={(m) => chatPhotoUrl(m.id, token)}
          placeholder={t("inbox.replyPlaceholder")}
          emptyHint={t("inbox.chatEmpty")}
          renderUnder={renderUnder}
        />
      </div>
    </div>
  );
}

export default function AdminChats({ token, onExit, theme, onThemeChange }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const [chats, setChats] = useState<ChatParticipant[] | null>(null);
  const [selected, setSelected] = useState<ChatParticipant | null>(null);

  useEffect(() => {
    if (selected) return;
    fetchAdminChats(token).then(setChats).catch(() => setChats([]));
  }, [token, selected]);

  if (selected) {
    return (
      <Conversation token={token} participant={selected} isRu={isRu}
        theme={theme} onThemeChange={onThemeChange} onBack={() => setSelected(null)} />
    );
  }

  const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleDateString(isRu ? "ru-RU" : "en-US", { day: "2-digit", month: "short" }) : "";

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("chat.adminTitle")} />
      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 640 }}>
        {chats === null ? (
          <div style={{ display: "grid", placeItems: "center", padding: 40 }}>
            <div className="spinner spinner-amber" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : chats.length === 0 ? (
          <div className="glass glass-lg" style={{ padding: 32, textAlign: "center" }}>
            <p className="t-sm muted">{t("chat.noChats")}</p>
          </div>
        ) : (
          <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
            {chats.map((c) => (
              <button key={c.user_id} onClick={() => setSelected(c)} className="glass glass-lg glass-sheen"
                style={{ textAlign: "left", cursor: "pointer", padding: 14, color: "inherit", border: "none", width: "100%", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ display: "grid", placeItems: "center", width: 42, height: 42, borderRadius: 12, flex: "none", background: "var(--violet-tint)", color: "var(--violet-text)" }}>
                  <Icon name="user" size={20} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row between" style={{ gap: 8 }}>
                    <span className="t-h" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                    <span className="t-xs muted-2" style={{ flex: "none" }}>{fmt(c.last_at)}</span>
                  </div>
                  <div className="t-sm muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.last_is_admin ? "Anya: " : ""}{c.last_has_photo && !c.last_body ? "📷" : c.last_body}
                  </div>
                  <IdentityLine p={c} style={{ marginTop: 3 }} />
                </div>
                {c.pending_work > 0 && (
                  <span className="badge badge-amber" style={{ flex: "none" }}>{c.pending_work} {t("chat.pending")}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
